import { useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Typography,
} from "@mui/material";
import { ReactElement, useCallback, useState } from "react";
import { Control, useController } from "react-hook-form";
import { FormattedMessage } from "react-intl";

import {
  ListSubaccountEscalationAndGeneralInboxesQuery,
  ListSubaccountEscalationAndGeneralInboxesQueryVariables,
  ThreadInboxFilterType,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import Table from "~frontend/components/Table";
import Select from "~frontend/components/forms/FormSelect";
import { LIST_SUBACCOUNT_ESCALATION_AND_GENERAL_INBOXES } from "~frontend/graphql/inboxes";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";

import { ThreadFilterAndSortValues } from "./schema";

export function InboxFilterDialog({
  control,
  open,
  onClose,
  onApply,
}: {
  control: Control<ThreadFilterAndSortValues, any, ThreadFilterAndSortValues>;
  open: boolean;
  onClose: () => void;
  onApply: () => Promise<void>;
}): ReactElement {
  const { subaccount } = useSubaccount()!;

  const { data, loading, error } = useQuery<
    ListSubaccountEscalationAndGeneralInboxesQuery,
    ListSubaccountEscalationAndGeneralInboxesQueryVariables
  >(LIST_SUBACCOUNT_ESCALATION_AND_GENERAL_INBOXES, {
    variables: { subaccountId: subaccount.id },
  });

  const { field: inboxesFilterField } = useController({
    control,
    name: "inboxes",
  });
  const { field: inboxFilterTypeField } = useController({
    control,
    name: "inboxFilterType",
  });

  const [selectedInboxes, setSelectedInboxes] = useState<
    { id: string; name: string | null }[]
  >(inboxesFilterField.value);

  const [filterType, setFilterType] = useState<ThreadInboxFilterType>(
    inboxFilterTypeField.value
  );

  const handleClose = useCallback(() => {
    onClose();
    setSelectedInboxes(inboxesFilterField.value);
    setFilterType(inboxFilterTypeField.value);
  }, [
    onClose,
    setSelectedInboxes,
    inboxesFilterField,
    setFilterType,
    inboxFilterTypeField,
  ]);

  const handleApply = useCallback(async () => {
    await inboxesFilterField.onChange(selectedInboxes);
    await inboxFilterTypeField.onChange(filterType);
    onClose();
    return onApply();
  }, [
    inboxesFilterField,
    selectedInboxes,
    inboxFilterTypeField,
    filterType,
    onClose,
    onApply,
  ]);

  return (
    <Dialog open={open}>
      <DialogTitle>
        <FormattedMessage
          description="Title for inbox filter dialog"
          defaultMessage="Filter by Inbox"
        />
      </DialogTitle>
      <DialogContent sx={{ width: "578px", paddingBottom: 0 }}>
        <Box
          sx={{
            backgroundColor: palette.lightGray,
            padding: "20px",
            marginBottom: "24px",
            pt: "16px",
            maxHeight: "432px",
            overflow: "scroll",
          }}
        >
          <AsyncWrapper loading={loading} errors={error} data={data}>
            {data => {
              const allInboxes = [
                ...data.listSubaccountEscalationInboxes.map(inbox => ({
                  ...inbox,
                  type: "escalation" as const,
                })),
                ...data.listSubaccountGeneralInboxes.map(inbox => ({
                  ...inbox,
                  type: "general" as const,
                })),
              ];

              return (
                <Table
                  size="small"
                  rows={allInboxes}
                  selectedRows={selectedInboxes.map(e => e.id)}
                  onChangeSelectedRows={ids => {
                    const idSet = new Set(ids);
                    setSelectedInboxes(
                      allInboxes
                        .filter(e => idSet.has(e.id))
                        .map(e => ({ ...e, name: e.name! }))
                    );
                  }}
                  columns={[
                    {
                      key: "name",
                      title: "Inbox Name",
                      renderValue: inbox => (
                        <FlexBox flexDirection="column">
                          {inbox.type === "escalation" ? (
                            <Chip
                              sx={{
                                height: "15px",
                                width: "70px",
                                borderRadius: 0,
                                backgroundColor: palette.lightOrange,
                                margin: 0,
                                "& .MuiChip-label": { padding: "0 4px" },
                              }}
                              label={
                                <Typography variant="h4Caps" component="span">
                                  <FormattedMessage
                                    defaultMessage="Escalation"
                                    description="Escalation inbox label"
                                  />
                                </Typography>
                              }
                            />
                          ) : null}
                          <Typography variant="body1Bold">
                            {inbox.name}
                          </Typography>
                        </FlexBox>
                      ),
                    },
                    {
                      key: "numThreads",
                      title: "Number of threads",
                      renderValue: inbox => (
                        <FormattedMessage
                          description="The number of threads in an inbox"
                          defaultMessage="{numThreads, plural, =1 {{numThreads} Thread} other {{numThreads} Threads}}"
                          values={{ numThreads: inbox.threadCount }}
                        />
                      ),
                    },
                    {
                      key: "numNeedsResponse",
                      title: "Number needing response",
                      renderValue: inbox => (
                        <Typography
                          variant={
                            inbox.needsReplyCount ? "body1Bold" : "body1"
                          }
                          sx={{
                            color: inbox.needsReplyCount ? palette.red : null,
                          }}
                        >
                          <FormattedMessage
                            description="The number of threads that need response in an inbox"
                            defaultMessage="{numNeedsResponse, plural, =1 {{numNeedsResponse} Needs Response} other {{numNeedsResponse} Need Response}}"
                            values={{
                              numNeedsResponse: inbox.needsReplyCount,
                            }}
                          />
                        </Typography>
                      ),
                    },
                  ]}
                  selectAllDisabled
                  noFooter
                />
              );
            }}
          </AsyncWrapper>
        </Box>
        <Select
          label={
            <FormattedMessage
              description="Label for the inbox status dropdown"
              defaultMessage="Inbox Status"
            />
          }
          value={filterType}
          onChange={event =>
            setFilterType(event.target.value as ThreadInboxFilterType)
          }
          fullWidth
        >
          <MenuItem
            key={ThreadInboxFilterType.Current}
            value={ThreadInboxFilterType.Current}
          >
            <FormattedMessage
              description="Dropdown option for current inboxes"
              defaultMessage="Only include threads currently in this inbox"
            />
          </MenuItem>
          <MenuItem
            key={ThreadInboxFilterType.Previous}
            value={ThreadInboxFilterType.Previous}
          >
            <FormattedMessage
              description="Dropdown option for previous inboxes"
              defaultMessage="Only include threads previously in this inbox"
            />
          </MenuItem>
          <MenuItem
            key={ThreadInboxFilterType.CurrentAndPrevious}
            value={ThreadInboxFilterType.CurrentAndPrevious}
          >
            <FormattedMessage
              description="Dropdown option for current and previous inboxes"
              defaultMessage="Include threads currently and previously in this inbox"
            />
          </MenuItem>
        </Select>
      </DialogContent>
      <DialogActions>
        <FlexBox
          justifyContent="space-between"
          sx={{ width: "100%", padding: "20px" }}
        >
          <Button
            variant="contained"
            color="tertiary"
            onClick={() => {
              setSelectedInboxes([]);
              setFilterType(ThreadInboxFilterType.Current);
            }}
          >
            <FormattedMessage
              description="Button label for clearing selections in inbox filter dialog"
              defaultMessage="Clear Selections"
            />
          </Button>

          <FlexBox gap={1}>
            <Button variant="contained" color="tertiary" onClick={handleClose}>
              <FormattedMessage
                description="Cancel button label"
                defaultMessage="Cancel"
              />
            </Button>
            <Button
              type="submit"
              onClick={handleApply}
              color="primary"
              variant="contained"
            >
              <FormattedMessage
                description="Button label for applying filters in inbox filter dialog"
                defaultMessage="Apply Filters"
              />
            </Button>
          </FlexBox>
        </FlexBox>
      </DialogActions>
    </Dialog>
  );
}
